import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
  Badge,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Fade,
  TablePagination,
  TableSortLabel,
  Menu,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from '@mui/material';
import { Document as PdfDocument, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import {
 Search as SearchIcon,
 Delete as DeleteIcon,
 Refresh as RefreshIcon,
 Add as AddIcon,
 MoreVert as MoreVertIcon,
 FileCopy as FileCopyIcon,
 Visibility as VisibilityIcon,
 Chat as ChatIcon,
 Business as BusinessIcon,
 Folder as FolderIcon,
 FilterList as FilterIcon,
 Clear as ClearIcon,
 Sort as SortIcon,
 CloudUpload as CloudUploadIcon,
 Description as DocumentIcon,
 PictureAsPdf as PdfIcon,
 Image as ImageIcon,
 TableChart as SpreadsheetIcon,
 Code as CodeIcon,
 InsertDriveFile as FileIcon,
 Download as DownloadIcon,
 Share as ShareIcon,
 Cancel as CancelIcon,
 UploadFile as UploadFileIcon,
 FilePresent as FilePresentIcon,
 Close as CloseIcon,
 Schedule as ScheduleIcon,
 Storage as StorageIcon,
 TrendingUp as TrendingUpIcon,
 ChevronRight as ChevronRightIcon,
 ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import api, { getProjects, getAllDocuments, getDeals, downloadFile, bulkDownloadFiles, bulkDeleteFiles, bulkShareDocuments, bulkCopyToProject, uploadFileToProject, uploadFileUnified, shareDocument, getMembers, getSharedDocuments, getAllProjectDocuments, getAllDealDocuments, getIntegrations, getIntegrationFiles, uploadUnifiedDocumentFromIntegration, deleteFile, getDocumentStats } from '../services/api';
import { Document, Project, DocumentSort, DocumentStats } from '../types';
import { Integration, IntegrationCategory, IntegrationStatus } from '../types/integrations';
import { formatBytes } from '../utils/formatters';
import DocumentDetailsModal from '../components/DocumentDetailsModal';
import { TableRowSkeleton, MetricCardSkeleton } from '../components/LoadingSkeleton';
import { useAuth } from '../contexts/AuthContext';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DocumentWorkspace: React.FC = () => {
  const { user } = useAuth();
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<Document | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState<string>('');
  const [documentToShare, setDocumentToShare] = useState<Document | null>(null);
  const [bulkShareDialogOpen, setBulkShareDialogOpen] = useState(false); // New state for bulk share dialog
  const [projects, setProjects] = useState<Project[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [documentType, setDocumentType] = useState<string>('other');
  const [uploadSelectedProject, setUploadSelectedProject] = useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true);
  const [dealsLoading, setDealsLoading] = useState<boolean>(true);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deals, setDeals] = useState<any[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [hasDealDocumentsSelected, setHasDealDocumentsSelected] = useState<boolean>(false); // New state for deal documents selection
  
  // Enhanced UI state
  const [sortField, setSortField] = useState<DocumentSort>(DocumentSort.UPLOADED_DESC);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sharedFilterType, setSharedFilterType] = useState<string>('all');
  const [sharedFilterStatus, setSharedFilterStatus] = useState<string>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [sharedDocsPage, setSharedDocsPage] = useState(0);
  const [sharedDocsRowsPerPage, setSharedDocsRowsPerPage] = useState(25);
  const [totalSharedDocuments, setTotalSharedDocuments] = useState(0);
  const [sharedDocsSort, setSharedDocsSort] = useState<DocumentSort>(DocumentSort.UPLOADED_DESC);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    document: Document | null;
  } | null>(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [downloadingDocIds, setDownloadingDocIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadTabValue, setUploadTabValue] = useState(0);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [noIntegrationsConnected, setNoIntegrationsConnected] = useState(false);
  const [integrationFiles, setIntegrationFiles] = useState<any[]>([]);
  const [integrationFilesLoading, setIntegrationFilesLoading] = useState(false);
  const [isSubfolderLoading, setIsSubfolderLoading] = useState(false);
  const [selectedIntegrationFilesForUpload, setSelectedIntegrationFilesForUpload] = useState<any[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([{ id: 'root', name: 'My Drive' }]);
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());
  const [uploadStatus, setUploadStatus] = useState<Map<string, 'pending' | 'uploading' | 'success' | 'error'>>(new Map());
  const [documentStats, setDocumentStats] = useState<DocumentStats | null>(null);
   const [passwordPrompt, setPasswordPrompt] = useState<{ file: File; fileName: string } | null>(null);
  const [password, setPassword] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);

  const tabs = useMemo(() => {
    const allTabs = [
        { id: 'all', label: 'All Documents', icon: <FolderIcon /> },
        { id: 'project', label: 'Project Documents', icon: <FolderIcon /> },
        { id: 'deal', label: 'Deal Documents', icon: <BusinessIcon /> },
        { id: 'ddq', label: 'DDQ Documents', icon: <DocumentIcon /> },
        { id: 'shared', label: 'Shared Documents', icon: <ShareIcon /> },
    ];
    const userPermissions = user?.permissions?.map(p => p.name) || [];
    const hasDdq = userPermissions.includes('ddq');
    const hasCim = userPermissions.includes('cim');

    return allTabs.filter(tab => {
      if (tab.id === 'deal') return hasCim;
      if (tab.id === 'ddq') return hasDdq;
      return true;
    });
  }, [user]);

  useEffect(() => {
    // if the current active tab is not in the visible tabs, default to the first one.
    if (!tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id || 'all');
    }
  }, [tabs, activeTab]);

   // Function to fetch documents based on the active tab and filters
   const fetchAllDocuments = useCallback(async () => {
     try {
      let allDocuments: any[] = [];
      
      console.log('Starting to fetch documents...');

      // Create filter parameters for API call
      const filterParams: any = {
        sort: sortField,
        page: page + 1, // API uses 1-based indexing
        size: rowsPerPage
      };
      
      // Add filter parameters if they are set
      if (filterType !== 'all') {
        filterParams.type = filterType;
      }
      
      if (filterStatus !== 'all') {
        filterParams.processing_status = filterStatus;
      }
      
      if (searchTerm) {
        filterParams.search = searchTerm;
      }
      
      if (selectedProject !== 'all') {
        filterParams.project_id = selectedProject;
      }

      // For "All Documents" tab, use a single API call that returns all documents with filters
      if (activeTab === 'all') {
        console.log('Fetching all documents with filters:', filterParams);
        const docsResponse = await getAllDocuments(filterParams);
        console.log('All documents API Response:', docsResponse);
        
        if (docsResponse && !Array.isArray(docsResponse)) {
          const { documents: docs = [], total = 0, pages = 1 } = docsResponse;
          allDocuments = [...docs];
          setTotalDocuments(total);
          console.log(`Got ${docs.length} documents from all sources (total: ${total}, pages: ${pages})`);
        }
      }
      // For "Project Documents" tab, only fetch project documents
      else if (activeTab === 'project') {
        console.log('Fetching project documents with filters:', filterParams);
        const projectDocsResponse = await getAllProjectDocuments(filterParams);
        console.log('Project documents API Response:', projectDocsResponse);
        
        if (projectDocsResponse && !Array.isArray(projectDocsResponse)) {
          const { documents: projectDocs = [], total = 0, pages = 1 } = projectDocsResponse;
          allDocuments = [...projectDocs];
          setTotalDocuments(total);
          console.log(`Got ${projectDocs.length} project documents (total: ${total}, pages: ${pages})`);
        }
      }
      // For "Deal Documents" tab, only fetch deal documents
      else if (activeTab === 'deal') {
        // Add deal_id to filterParams if a deal is selected
        if (selectedDeal !== 'all') {
          filterParams.deal_id = selectedDeal;
        }
        
        // Use the dedicated function to fetch all deal documents
        const dealDocsResponse = await getAllDealDocuments(filterParams);
        if (dealDocsResponse && !Array.isArray(dealDocsResponse)) {
          const { documents: dealDocs = [], total = 0, pages = 1 } = dealDocsResponse;
          allDocuments = [...dealDocs];
          setTotalDocuments(total);
          console.log(`Got ${dealDocs.length} deal documents (total: ${total}, pages: ${pages})`);
        }
      }
      // For "DDQ Documents" tab, only fetch DDQ documents
      else if (activeTab === 'ddq') {
        console.log('Fetching DDQ documents with filters:', filterParams);
        filterParams.type = 'ddq';
        const docsResponse = await getAllDocuments(filterParams);
        console.log('DDQ documents API Response:', docsResponse);
        if (docsResponse && !Array.isArray(docsResponse)) {
          const { documents: docs = [], total = 0, pages = 1 } = docsResponse;
          allDocuments = [...docs];
          setTotalDocuments(total);
          console.log(`Got ${docs.length} DDQ documents (total: ${total}, pages: ${pages})`);
        }
      }
      // For "Shared Documents" tab, the handling is done in handleTabChange
      
      console.log(`Final total documents fetched: ${allDocuments.length}`);

      // Store all documents
      setDocuments(allDocuments);
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    }
  }, [sortField, page, rowsPerPage, filterType, filterStatus, searchTerm, selectedProject, selectedDeal, activeTab, setTotalDocuments, setDocuments]);

  const fetchDocumentStats = async () => {
    try {
      const stats = await getDocumentStats();
      setDocumentStats(stats);
    } catch (error) {
      console.error('Error fetching document stats:', error);
    }
  };

  // Function to fetch deals
  const fetchDeals = async () => {
    try {
      const dealsResponse = await getDeals({
        page: 1,
        size: 100,
      });
      
      if (dealsResponse && dealsResponse.deals) {
        setDeals(dealsResponse.deals);
      } else {
        setDeals([]);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
      setDeals([]);
    }
  };
  // Function to fetch projects
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const projectsResponse = await getProjects({
        page: 1,
        size: 1000,
      });
      if (projectsResponse && projectsResponse.projects) {
        setProjects(projectsResponse.projects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchSharedDocuments = useCallback(async (
    sharedFilterType?: string,
    sharedFilterStatus?: string
  ) => {
    setFilesLoading(true);
    try {
      const params: any = {
        page: sharedDocsPage + 1,
        size: sharedDocsRowsPerPage,
        sort: sharedDocsSort,
      };

      if (sharedFilterType && sharedFilterType !== 'all') {
        params.type = sharedFilterType;
      }

      if (sharedFilterStatus && sharedFilterStatus !== 'all') {
        params.processing_status = sharedFilterStatus;
      }

      const sharedRecords = await getSharedDocuments(params);
      
      if (sharedRecords && sharedRecords.documents) {
        const formattedDocs = sharedRecords.documents.map((doc: any) => ({
          ...doc,
          name: doc.filename || doc.name,
          type: doc.type || 'other',
          source_name: doc.project_name ? 'Project' :
                       doc.deal_name ? 'Deal' : 'Shared with me',
          source_type: doc.source || 'shared',
          file_size: doc.file_size,
          mime_type: doc.mime_type,
          uploader: doc.uploader_name,
          processing_status: doc.processing_status || 'completed',
          embedding_status: doc.embedding_status || 'pending',
          size: doc.file_size,
          tags: doc.tags || [],
        }));
        setDocuments(formattedDocs);
        
        // Update pagination information
        if (sharedRecords.total !== undefined) {
          setTotalSharedDocuments(sharedRecords.total);
        }
      } else {
        setDocuments([]);
      }
    } catch (error: any) {
      console.error('Error fetching shared documents:', error);
      setDocuments([]);
    } finally {
      setFilesLoading(false);
    }
  }, [sharedDocsPage, sharedDocsRowsPerPage, sharedDocsSort, setDocuments, setTotalSharedDocuments, setFilesLoading]);


  useEffect(() => {
    const fetchInitialData = () => {
      // "Shared Documents" tab is handled separately in handleTabChange
      if (activeTab === 'shared') {
        return;
      }

      setFilesLoading(true);
      
      fetchAllDocuments()
        .catch((err: any) => console.error('Error fetching documents:', err))
        .finally(() => {
          setFilesLoading(false);
          if (projectsLoading) setProjectsLoading(false);
          if (dealsLoading) setDealsLoading(false);
        });
    };

    fetchInitialData();
  }, [activeTab, sortField, page, rowsPerPage, dealsLoading, fetchAllDocuments, projectsLoading]);

  useEffect(() => {
    setStatsLoading(true);
    fetchDocumentStats()
      .catch((err: any) => console.error('Error fetching document stats:', err))
      .finally(() => setStatsLoading(false));
  }, []);
  
  // Effect to refetch documents when filter values change
  useEffect(() => {
    // Skip initial render
    if (dealsLoading) {
      return;
    }
    
    // Fetch documents with updated filters
    const fetchDocumentsWithFilters = async () => {
      setFilesLoading(true);
      try {
        await fetchAllDocuments();
      } catch (error: any) {
        console.error('Error fetching documents with filters:', error);
      } finally {
        setFilesLoading(false);
      }
    };
    
    // Debounce the API call to prevent too many requests
    const timeoutId = setTimeout(() => {
      fetchDocumentsWithFilters();
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [filterType, filterStatus, searchTerm, selectedProject, selectedDeal, dealsLoading, fetchAllDocuments]);

  useEffect(() => {
    if (activeTab === 'shared') {
      fetchSharedDocuments(sharedFilterType, sharedFilterStatus);
    }
  }, [sharedDocsPage, sharedDocsRowsPerPage, sharedDocsSort, activeTab, sharedFilterType, sharedFilterStatus, fetchSharedDocuments]);

  useEffect(() => {
    const checkDealDocuments = () => {
      if (selectedDocuments.size === 0) {
        setHasDealDocumentsSelected(false);
        return;
      }

      const anyDealDoc = Array.from(selectedDocuments).some(docId => {
        const doc = documents.find(d => d.id === docId);
        return doc && (doc.deal_id || doc.source_type === 'deal');
      });
      setHasDealDocumentsSelected(anyDealDoc);
    };

    checkDealDocuments();
  }, [selectedDocuments, documents]);

  // This function is no longer needed as we're fetching the correct documents directly from the API
  // based on the active tab

  const handleTabChange = async (event: React.SyntheticEvent, newTab: string) => {
    setActiveTab(newTab);
    setSelectedDocuments(new Set());
    setPage(0);
    setSharedDocsPage(0);
    setSelectedDeal('all'); // Reset selected deal when changing tabs

    if (newTab === 'project') { // Project Documents tab
      // Fetch projects in the background
      fetchProjects();
    } else if (newTab === 'deal') { // Deal Documents tab
      // Ensure deals are loaded for the dropdown
      if (deals.length === 0) {
        await fetchDeals();
      }
    }
    // For other tabs, the useEffect hook will handle fetching the documents
  };

  const handleUploadTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setUploadTabValue(newValue);
    setSelectedFiles([]);
    setSelectedIntegrationFilesForUpload([]);
  };

  // Handle deal selection in the dropdown
  const handleDealChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
    const dealId = event.target.value as string;
    setSelectedDeal(dealId);
  };

  const handleProjectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const projectId = event.target.value as string;
    setSelectedProject(projectId);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDocuments(newSelected);
  };

  const isSelected = (id: string) => selectedDocuments.has(id);

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterStatus('all');
    setSharedFilterType('all');
    setSharedFilterStatus('all');
    setSelectedProject('all');
    setSelectedDeal('all');
  };

  const hasActiveFilters = searchTerm || filterType !== 'all' || filterStatus !== 'all' || sharedFilterType !== 'all' || sharedFilterStatus !== 'all' || selectedDeal !== 'all' || selectedProject !== 'all';

  const getStatusChip = (status: 'completed' | 'processing' | 'failed' | 'uploaded' | 'pending') => {
    const statusConfig = {
      completed: { label: 'Completed', color: 'success' as const },
      processing: { label: 'Processing', color: 'info' as const },
      failed: { label: 'Failed', color: 'error' as const },
      uploaded: { label: 'Uploaded', color: 'warning' as const },
      pending: { label: 'Pending', color: 'info' as const },
    };
    
    const config = statusConfig[status] || { label: 'Unknown', color: 'default' as const };
    return <Chip label={config.label} color={config.color} size="small" variant="filled" />;
  };

  const getFileIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'pdf': return <PdfIcon color="error" />;
      case 'doc':
      case 'docx': return <DocumentIcon color="primary" />;
      case 'xls':
      case 'xlsx': return <SpreadsheetIcon color="success" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <ImageIcon color="info" />;
      case 'txt':
      case 'md': return <CodeIcon color="action" />;
      default: return <FileIcon color="action" />;
    }
  };


  const handleOpenModal = (doc: Document) => {
    setSelectedDocument(doc);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedDocument(null);
    setIsModalOpen(false);
  };

  const handleOpenPdfViewer = async (doc: Document) => {
    // Check if document is a PDF
    const isPdf = doc.filename?.toLowerCase().endsWith('.pdf') || 
                  doc.name?.toLowerCase().endsWith('.pdf') ||
                  doc.mime_type === 'application/pdf';
    
    if (!isPdf) {
      // If not a PDF, open the details modal instead
      handleOpenModal(doc);
      return;
    }
    
    setPdfDocument(doc);
    setPdfViewerOpen(true);
    setPdfPageNumber(1);
    setPdfScale(1.0);
    
    // Fetch the PDF with authentication and create a blob URL
    try {
      const token = localStorage.getItem('token');
      const organization = localStorage.getItem('organization');
      let organizationId = '';
      
      if (organization) {
        try {
          const org = JSON.parse(organization);
          organizationId = org.id;
        } catch (e) {
          console.error('Failed to parse organization:', e);
        }
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/documents/${doc.id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Organization-ID': organizationId,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Error loading PDF:', error);
      setPdfUrl(null);
    }
  };

  const handleClosePdfViewer = () => {
    // Revoke the blob URL to free memory
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfDocument(null);
    setPdfViewerOpen(false);
    setPdfNumPages(null);
    setPdfPageNumber(1);
  };

  const onPdfLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPdfNumPages(numPages);
  };

  const onPdfLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
  };

  const changePdfPage = (offset: number) => {
    setPdfPageNumber(prevPageNumber => {
      const newPage = prevPageNumber + offset;
      if (pdfNumPages) {
        return Math.min(Math.max(1, newPage), pdfNumPages);
      }
      return newPage;
    });
  };

  const zoomIn = () => {
    setPdfScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setPdfScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleRefresh = () => {
    setStatsLoading(true);

    if (activeTab === 'shared') {
      // fetchSharedDocuments handles its own filesLoading state
      fetchSharedDocuments(sharedFilterType, sharedFilterStatus)
        .catch((error: any) => console.error('Error refreshing shared documents:', error));
    } else {
      setFilesLoading(true);
      fetchAllDocuments()
        .catch((error: any) => console.error('Error refreshing documents:', error))
        .finally(() => setFilesLoading(false));
    }

    fetchDocumentStats()
      .catch(error => console.error('Error refreshing stats:', error))
      .finally(() => setStatsLoading(false));
  };

  // Handle context menu (right-click or three-dot menu)
  const handleContextMenu = (event: React.MouseEvent | null, document: Document, position?: { x: number, y: number }) => {
    // If it's a real mouse event (right-click), prevent default and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Get position either from provided position or from the event
    const mouseX = position ? position.x : event ? event.clientX : 0;
    const mouseY = position ? position.y : event ? event.clientY : 0;
    
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: mouseX + 2,
            mouseY: mouseY - 6,
            document,
          }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleDownload = async (doc: Document) => {
    console.log('Downloading document:', doc);
    if (!doc || !doc.id) {
      console.error('Invalid document or document ID for download');
      return;
    }
    
    // Add document ID to downloading set to show loading indicator
    setDownloadingDocIds(prev => new Set(prev).add(doc.id));
    
    try {
      await downloadFile(doc.id, doc.filename || doc.name);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Optionally, show a user-facing error message
    } finally {
      // Remove document ID from downloading set when done
      setDownloadingDocIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(doc.id);
        return newSet;
      });
    }
  };

  const handleBulkDownload = async () => {
    setIsBulkDownloading(true);
    try {
      const documentIds = Array.from(selectedDocuments);
      await bulkDownloadFiles(documentIds);
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Selected documents downloaded as a zip file.' }));
    } catch (error) {
      console.error('Error during bulk download:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to download documents.' }));
    } finally {
      setIsBulkDownloading(false);
      setSelectedDocuments(new Set()); // Clear selection after bulk download
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    setIsDeleting(true);
    try {
      await deleteFile(documentId);
      handleRefresh(); // Refresh the document list after deletion
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setIsDeleting(false);
    }
  };


  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setDocumentType('other');
    setUploadSelectedProject('');
    setUploadTabValue(0); // Reset to "Upload New" tab
    setSelectedIntegration(null); // Clear selected integration
    setIntegrationFiles([]); // Clear integration files
    setSelectedIntegrationFilesForUpload([]); // Clear selected integration files for upload
    setCurrentFolderId('root'); // Reset current folder to root
    setFolderPath([{ id: 'root', name: 'My Drive' }]); // Reset folder path
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      if (files.length > 5) {
        window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'You can only upload a maximum of 5 files at a time.' }));
        return;
      }
      setSelectedFiles(files);
    }
  };

  const handleDragEvents = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    handleDragEvents(e);
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    handleDragEvents(e);
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    handleDragEvents(e);
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 5) {
        window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'You can only upload a maximum of 5 files at a time.' }));
        return;
      }
      setSelectedFiles(files);
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleSelectFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  const handlePasswordSubmit = async () => {
    if (!passwordPrompt || !password.trim()) return;

    setIsCheckingPassword(true);

    try {
      const file = passwordPrompt.file;
      
      // Update status to uploading
      setUploadStatus(prev => new Map(prev).set(file.name, 'uploading'));
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev.get(file.name) || 0;
          if (current < 90) {
            return new Map(prev).set(file.name, current + 10);
          }
          return prev;
        });
      }, 200);
      
      // Upload with password
      const formData = new FormData();
      formData.append('file', file);
      formData.append('password', password);
      formData.append('name', file.name);
      formData.append('type', documentType);
      if (uploadSelectedProject) {
        formData.append('project_id', uploadSelectedProject);
      }
      
      await api.post('/api/v1/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      clearInterval(progressInterval);
      
      // Set to 100% and mark as success
      setUploadProgress(prev => new Map(prev).set(file.name, 100));
      setUploadStatus(prev => new Map(prev).set(file.name, 'success'));
      
      // Clear password prompt
      setPasswordPrompt(null);
      setPassword('');
      
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: `${file.name} uploaded successfully` }));
      
    } catch (error: any) {
      console.error('Password upload error:', error);
      
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.startsWith('incorrect_password:')) {
        window.dispatchEvent(new CustomEvent('apiError', { detail: 'Incorrect password. Please try again.' }));
      } else {
        if (passwordPrompt) {
          setUploadStatus(prev => new Map(prev).set(passwordPrompt.fileName, 'error'));
        }
        window.dispatchEvent(new CustomEvent('apiError', { detail: `Failed to upload: ${errorDetail || error.message}` }));
        setPasswordPrompt(null);
        setPassword('');
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    if (passwordPrompt) {
      setUploadStatus(prev => new Map(prev).set(passwordPrompt.fileName, 'error'));
    }
    setPasswordPrompt(null);
    setPassword('');
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    
    // Initialize progress and status for all files
    const newProgress = new Map<string, number>();
    const newStatus = new Map<string, 'pending' | 'uploading' | 'success' | 'error'>();
    
    selectedFiles.forEach(file => {
      newProgress.set(file.name, 0);
      newStatus.set(file.name, 'pending');
    });
    
    setUploadProgress(newProgress);
    setUploadStatus(newStatus);
    
    try {
      // Upload files sequentially (one at a time)
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        // Update status to uploading
        setUploadStatus(prev => new Map(prev).set(file.name, 'uploading'));
        
        try {
          // Simulate progress updates (since we don't have real progress from API)
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
              const current = prev.get(file.name) || 0;
              if (current < 90) {
                return new Map(prev).set(file.name, current + 10);
              }
              return prev;
            });
          }, 200);
          
          // Upload single file
          if (uploadSelectedProject) {
            await uploadFileToProject(uploadSelectedProject, [file], documentType, undefined);
          } else {
            await uploadFileUnified([file], documentType, undefined);
          }
          
          clearInterval(progressInterval);
          
          // Set to 100% and mark as success
          setUploadProgress(prev => new Map(prev).set(file.name, 100));
          setUploadStatus(prev => new Map(prev).set(file.name, 'success'));
          
        } catch (error: any) {
          console.error(`Upload failed for ${file.name}:`, error);
          
          // Check if error is due to password protection
          const errorDetail = error.response?.data?.detail || error.response?.data?.message || '';
          if (errorDetail.startsWith('password_required:')) {
            setPasswordPrompt({ file, fileName: file.name });
            setIsUploading(false);
            return;
          }
          
          setUploadStatus(prev => new Map(prev).set(file.name, 'error'));
        }
      }
      
      // Wait a moment to show completion, then close
      await new Promise(resolve => setTimeout(resolve, 1000));
      handleCloseUploadDialog();
      handleRefresh(); // Refresh the document list
      
      // Reset progress tracking
      setUploadProgress(new Map());
      setUploadStatus(new Map());
      
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleIntegrationUpload = async () => {
    if (selectedIntegrationFilesForUpload.length === 0 || !selectedIntegration) return;

    setIsUploading(true);
    try {
      const toolName = selectedIntegration.name.toLowerCase().replace(/\s/g, '');
      const fileIds = selectedIntegrationFilesForUpload.map(f => f.id);
      await uploadUnifiedDocumentFromIntegration(toolName, fileIds, documentType);
      handleCloseUploadDialog();
      await handleRefresh(); // Refresh the document list
    } catch (error) {
      console.error('Upload from integration failed:', error);
      // Optionally, show an error message to the user
    } finally {
      setIsUploading(false);
    }
  };
 
   const [copyDialogOpen, setCopyDialogOpen] = useState(false);
   const [documentToCopy, setDocumentToCopy] = useState<Document | null>(null);
   const [targetProject, setTargetProject] = useState<string>('');
   const [bulkCopyDialogOpen, setBulkCopyDialogOpen] = useState(false); // New state for bulk copy dialog
   const [isCopying, setIsCopying] = useState(false);
   const [dealShareCopyDialogOpen, setDealShareCopyDialogOpen] = useState(false);
   const [dealShareCopyDocument, setDealShareCopyDocument] = useState<Document | null>(null);
 
   const handleOpenDealShareCopyDialog = (doc: Document) => {
     setDealShareCopyDocument(doc);
     setDealShareCopyDialogOpen(true);
   };
 
   const handleCloseDealShareCopyDialog = () => {
     setDealShareCopyDialogOpen(false);
     setDealShareCopyDocument(null);
   };
 
   const handleOpenShareDialog = async (doc: Document) => {
     setDocumentToShare(doc);
    setShareDialogOpen(true);
    setIsFetchingMembers(true);
    try {
      const memberListResponse = await getMembers();
      const memberList = memberListResponse.members || [];
      if (user) {
        setMembers(memberList.filter((member: any) => member.user_email !== user.email));
      } else {
        setMembers(memberList);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setIsFetchingMembers(false);
    }
  };

  const handleOpenCopyDialog = (doc: Document) => {
    setDocumentToCopy(doc);
    setCopyDialogOpen(true);
    setTargetProject(''); // Reset target project
  };

  const handleCloseCopyDialog = () => {
    setCopyDialogOpen(false);
    setDocumentToCopy(null);
    setTargetProject('');
  };

  const handleCopyDocument = async () => {
    if (!documentToCopy || !targetProject) return;

    setIsCopying(true);
    try {
      await api.post(`/api/v1/documents/${documentToCopy.id}/copy`, {
        target_project_id: targetProject,
      });
      handleCloseCopyDialog();
      handleRefresh(); // Refresh the document list after copying
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: `Document "${documentToCopy.filename || documentToCopy.name}" copied successfully.` }));
    } catch (error) {
      console.error('Error copying document:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to copy document.' }));
    } finally {
      setIsCopying(false);
    }
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setDocumentToShare(null);
    setSelectedMember('');
  };

  const handleShareDocument = async () => {
    if (documentToShare && selectedMember) {
      setIsSharing(true);
      try {
        await shareDocument(documentToShare.id, selectedMember);
        handleCloseShareDialog();
        // Optionally, show a success message
      } catch (error) {
        console.error('Error sharing document:', error);
        // Optionally, show an error message
      } finally {
        setIsSharing(false);
      }
    }
  };

  const handleOpenBulkShareDialog = async () => {
    setBulkShareDialogOpen(true);
    setIsFetchingMembers(true);
    try {
      const memberListResponse = await getMembers();
      const memberList = memberListResponse.members || [];
      if (user) {
        setMembers(memberList.filter((member: any) => member.user_email !== user.email));
      } else {
        setMembers(memberList);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setIsFetchingMembers(false);
    }
  };

  const handleCloseBulkShareDialog = () => {
    setBulkShareDialogOpen(false);
    setSelectedMember('');
  };

  const handleBulkShareDocuments = async () => {
    if (selectedDocuments.size === 0 || !selectedMember) return;

    setIsSharing(true);
    try {
      const documentIds = Array.from(selectedDocuments);
      await bulkShareDocuments(documentIds, selectedMember);
      handleCloseBulkShareDialog();
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Documents shared successfully.' }));
      setSelectedDocuments(new Set()); // Clear selection after bulk share
    } catch (error) {
      console.error('Error bulk sharing documents:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to share documents.' }));
    } finally {
      setIsSharing(false);
    }
  };

  const handleOpenBulkCopyDialog = async () => {
    setBulkCopyDialogOpen(true);
    setProjectsLoading(true);
    try {
      const projectsResponse = await getProjects({
        page: 1,
        size: 1000,
      });
      if (projectsResponse && projectsResponse.projects) {
        setProjects(projectsResponse.projects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects for bulk copy:', error);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleCloseBulkCopyDialog = () => {
    setBulkCopyDialogOpen(false);
    setTargetProject('');
  };

  const handleBulkCopyToProject = async () => {
    if (selectedDocuments.size === 0 || !targetProject) return;

    setIsCopying(true);
    try {
      const documentIds = Array.from(selectedDocuments);
      await bulkCopyToProject(documentIds, targetProject);
      handleCloseBulkCopyDialog();
      handleRefresh(); // Refresh the document list after copying
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: `Selected documents copied to project successfully.` }));
      setSelectedDocuments(new Set()); // Clear selection after bulk copy
    } catch (error) {
      console.error('Error bulk copying documents:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to copy documents.' }));
    } finally {
      setIsCopying(false);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIntegrationsLoading(true);
        const integrationsResponse = await getIntegrations();

        if (integrationsResponse && integrationsResponse.length === 0) {
          setNoIntegrationsConnected(true);
          setIntegrations([]);
        } else {
          setNoIntegrationsConnected(false);
          const integrations = integrationsResponse.map((apiIntegration: { id: string; tool_name: string }) => {
            const displayName = apiIntegration.tool_name.charAt(0).toUpperCase() + apiIntegration.tool_name.slice(1);
            
            return {
              id: apiIntegration.id,
              name: displayName,
              description: `${displayName} integration`,
              category: IntegrationCategory.DocumentRepository,
              status: IntegrationStatus.Connected,
              isConnected: true,
            };
          });
          setIntegrations(integrations);
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        setNoIntegrationsConnected(true); // Assume no integrations if API call fails
        setIntegrations([]);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    if (uploadDialogOpen) {
      fetchInitialData();
      fetchProjects(); // Fetch projects when the upload dialog opens
    }
  }, [uploadDialogOpen]);

  useEffect(() => {
    if (copyDialogOpen) {
      fetchProjects(); // Fetch projects when the copy dialog opens
    }
  }, [copyDialogOpen]);

  useEffect(() => {
    if (selectedIntegration) {
      fetchIntegrationFiles(selectedIntegration, currentFolderId);
    }
  }, [selectedIntegration, currentFolderId]);

  const fetchIntegrationFiles = async (integration: Integration, folderId: string = 'root') => {
    if (!integration.isConnected) {
      return;
    }
    if (folderId === 'root') {
      setIntegrationFilesLoading(true);
    } else {
      setIsSubfolderLoading(true);
    }
    setIntegrationFiles([]);
    try {
      const integrationName = integration.name.toLowerCase().replace(/\s/g, '');
      const response = await getIntegrationFiles(integrationName, folderId);
      
      if (response.status === 200) {
          const data = response.data;
          let files: any[] = [];
          if (integrationName === 'googledrive') {
            files = (data.files || []).map((file: any) => ({
                id: file.id,
                name: file.name,
                modified_time: file.modifiedTime,
                size: file.size ? parseInt(file.size, 10) : 0,
                mimeType: file.mimeType,
                isFolder: file.mimeType === 'application/vnd.google-apps.folder',
              }));
          } else if (integrationName === 'dropbox') {
            files = (data.entries || []).map((entry: any) => ({
                id: entry.path_display,
                name: entry.name,
                modified_time: entry.server_modified,
                size: entry.size,
                isFolder: entry['.tag'] === 'folder',
              }));
          } else if (integrationName === 'box') {
            files = (data.entries || []).map((entry: any) => ({
                id: entry.id,
                name: entry.name,
                modified_time: entry.modified_at,
                size: entry.size,
                isFolder: entry.type === 'folder',
              }));
           } else if (integrationName === 'onedrive') {
             files = (data.value || []).map((entry: any) => ({
               id: entry.id,
               name: entry.name,
               modified_time: entry.lastModifiedDateTime,
               size: entry.size,
               isFolder: !!entry.folder,
             }));
           }
          setIntegrationFiles(files);
      } else {
          console.error(`Failed to fetch files for ${integration.name}`);
          setIntegrationFiles([]);
      }
    } catch (error) {
      console.error(`Error fetching files for ${integration.name}:`, error);
      setIntegrationFiles([]);
    } finally {
      setIntegrationFilesLoading(false);
      setIsSubfolderLoading(false);
    }
  };

  const handleIntegrationFileSelect = (file: any, isSelected: boolean) => {
    if (isSelected) {
      const newFile = {
        id: file.id,
        name: file.name,
        size: file.size || 0,
        type: file.mimeType || 'application/octet-stream',
        source: selectedIntegration?.name,
      };
      setSelectedIntegrationFilesForUpload(prev => [...prev, newFile]);
    } else {
      setSelectedIntegrationFilesForUpload(prev => prev.filter(f => f.id !== file.id));
    }
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {/* Enhanced Header */}
        <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Document Workspace</Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{ ml: 1 }}
              onClick={handleOpenUploadDialog}
            >
              Upload Documents
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={filesLoading}
              sx={{ ml: 1 }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

          {/* Document Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {(statsLoading) ? (
              Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <MetricCardSkeleton />
                </Grid>
              ))
            ) : (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: 'primary.50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StorageIcon color="primary" />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {documentStats?.total_documents ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Documents
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: 'success.50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon color="success" />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {documentStats?.processed ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Processed
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: 'warning.50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon color="warning" />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {documentStats?.processing ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Processing
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: 'info.50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StorageIcon color="info" />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatBytes(documentStats?.total_size ?? 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Size
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>

        <Paper sx={{ borderRadius: 2, overflow: 'hidden', height: '70vh', display: 'flex', flexDirection: 'column' }}>
          {/* Tabs for different document sources */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="document source tabs">
              {tabs.map(tab => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
  
          {/* Enhanced Search and Filters */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <TextField
                placeholder="Search documents and tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 300,
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Tooltip title="Toggle Filters">
                <IconButton
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  color={hasActiveFilters ? 'primary' : 'default'}
                  sx={{
                    border: hasActiveFilters ? '2px solid' : '1px solid',
                    borderColor: hasActiveFilters ? 'primary.main' : 'divider',
                  }}
                >
                  <Badge badgeContent={hasActiveFilters ? '!' : 0} color="error">
                    <FilterIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={activeTab === 'ddq' ? sharedDocsSort : sortField}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    if (activeTab === 'shared') {
                      const isAsc = sharedDocsSort.endsWith('asc');
                      setSharedDocsSort(isAsc ? value.replace('asc', 'desc') : value.replace('desc', 'asc'));
                    } else {
                      const isAsc = sortField.endsWith('asc');
                      setSortField(isAsc ? value.replace('asc', 'desc') : value.replace('desc', 'asc'));
                    }
                  }
                }}
                size="small"
              >
                <ToggleButton value={DocumentSort.UPLOADED_ASC}>
                  <Tooltip title="Sort by Date">
                    <ScheduleIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={DocumentSort.NAME_ASC}>
                  <Tooltip title="Sort by Name">
                    <DocumentIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={DocumentSort.SIZE_ASC}>
                  <Tooltip title="Sort by Size">
                    <StorageIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <IconButton
                onClick={() => {
                  if (activeTab === 'shared') {
                    const isAsc = sharedDocsSort.endsWith('asc');
                    setSharedDocsSort(isAsc ? sharedDocsSort.replace('asc', 'desc') as DocumentSort : sharedDocsSort.replace('desc', 'asc') as DocumentSort);
                  } else {
                    const isAsc = sortField.endsWith('asc');
                    setSortField(isAsc ? sortField.replace('asc', 'desc') as DocumentSort : sortField.replace('desc', 'asc') as DocumentSort);
                  }
                }}
                color="primary"
              >
                <Tooltip title={`Sort ${(activeTab === 'shared' ? sharedDocsSort : sortField).endsWith('asc') ? 'Descending' : 'Ascending'}`}>
                  <SortIcon sx={{ transform: (activeTab === 'shared' ? sharedDocsSort : sortField).endsWith('desc') ? 'rotate(180deg)' : 'none' }} />
                </Tooltip>
              </IconButton>

              <Divider orientation="vertical" flexItem />

            </Box>

            {/* Expandable Filters with Deal Dropdown for Deal Documents tab */}
            {filtersOpen && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Project Dropdown - Show on Project Documents tab */}
                {activeTab === 'project' && (
                  <FormControl size="small" sx={{ minWidth: 200, marginLeft: 4 }}>
                    <InputLabel>Select Project</InputLabel>
                    <Select
                      value={selectedProject}
                      onChange={(e: any) => handleProjectChange(e)}
                      label="Select Project"
                    >
                      <MenuItem value="all">
                        <em>All Projects</em>
                      </MenuItem>
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Deal Dropdown - Only show when Deal Documents tab is active */}
                {activeTab === 'deal' && (
                  <FormControl size="small" sx={{ minWidth: 200, marginLeft: 4 }}>
                    <InputLabel>Select Deal</InputLabel>
                    <Select
                      value={selectedDeal}
                      onChange={(e: any) => handleDealChange(e)}
                      label="Select Deal"
                    >
                      <MenuItem value="all">
                        <em>All Deals</em>
                      </MenuItem>
                      {deals.map((deal) => (
                        <MenuItem key={deal.id} value={deal.id}>
                          {deal.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Document Type and Status Filters for All/Project/Deal tabs */}
                {(activeTab === 'all' || activeTab === 'project' || activeTab === 'deal') && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 120, marginLeft: 4 }}>
                      <InputLabel>Document Type</InputLabel>
                      <Select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        label="Document Type"
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="lease">Lease</MenuItem>
                        <MenuItem value="jv_agreement">JV Agreement</MenuItem>
                        <MenuItem value="psa">PSA</MenuItem>
                        <MenuItem value="loan_doc">Loan Doc</MenuItem>
                        <MenuItem value="offering_memorandum">Offering Memorandum</MenuItem>
                        <MenuItem value="sponsor_model">Sponsor Model</MenuItem>
                        <MenuItem value="ddq">DDQ</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        label="Status"
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}

                {/* Status Filters for DDQ tab */}
                {activeTab === 'ddq' && (
                  <FormControl size="small" sx={{ minWidth: 120, marginLeft: 4 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="needs_review">Needs Review</MenuItem>
                      <MenuItem value="not_started">Not Started</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Document Type and Status Filters for Shared Documents tab */}
                {activeTab === 'shared' && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 120, marginLeft: 4 }}>
                      <InputLabel>Document Type</InputLabel>
                      <Select
                        value={sharedFilterType}
                        onChange={(e) => setSharedFilterType(e.target.value)}
                        label="Document Type"
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="lease">Lease</MenuItem>
                        <MenuItem value="jv_agreement">JV Agreement</MenuItem>
                        <MenuItem value="psa">PSA</MenuItem>
                        <MenuItem value="loan_doc">Loan Doc</MenuItem>
                        <MenuItem value="offering_memorandum">Offering Memorandum</MenuItem>
                        <MenuItem value="sponsor_model">Sponsor Model</MenuItem>
                        <MenuItem value="ddq">DDQ</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={sharedFilterStatus}
                        onChange={(e) => setSharedFilterStatus(e.target.value)}
                        label="Status"
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}

                {(hasActiveFilters || selectedDeal !== 'all') && (
                  <Button
                    startIcon={<ClearIcon />}
                    onClick={clearAllFilters}
                    size="small"
                    color="error"
                    variant="outlined"
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Bulk Actions */}
          {selectedDocuments.size > 0 && (
            <Box sx={{ p: 2, bgcolor: 'primary.50', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedDocuments.size} documents selected
                </Typography>
                <Tooltip title={hasDealDocumentsSelected ? "Cannot perform bulk actions on deal documents" : ""}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileCopyIcon />}
                      onClick={handleOpenBulkCopyDialog}
                      disabled={hasDealDocumentsSelected}
                    >
                      Copy to Project
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={hasDealDocumentsSelected ? "Cannot perform bulk actions on deal documents" : ""}>
                  <span>
                    <Button variant="outlined" size="small" startIcon={<ShareIcon />} onClick={handleOpenBulkShareDialog} disabled={hasDealDocumentsSelected}>
                      Share
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={hasDealDocumentsSelected ? "Cannot perform bulk actions on deal documents" : ""}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isBulkDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                      onClick={handleBulkDownload}
                      disabled={isBulkDownloading || hasDealDocumentsSelected}
                    >
                      {isBulkDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={hasDealDocumentsSelected ? "Cannot perform bulk actions on deal documents" : ""}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                      onClick={async () => {
                        setIsDeleting(true);
                        try {
                          const documentIds = Array.from(selectedDocuments);
                          await bulkDeleteFiles(documentIds);
                          window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Selected documents deleted successfully.' }));
                          handleRefresh(); // Refresh the document list after deletion
                          setSelectedDocuments(new Set()); // Clear selection after bulk delete
                        } catch (error) {
                          console.error('Error during bulk delete:', error);
                          window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to delete documents.' }));
                        } finally {
                          setIsDeleting(false);
                        }
                      }}
                      disabled={isDeleting || hasDealDocumentsSelected}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setSelectedDocuments(new Set())}
                >
                  Clear Selection
                </Button>
              </Box>
            </Box>
          )}

          {/* Loading State */}
          {filesLoading && (
            <Box sx={{ p: 1 }}>
              <LinearProgress />
            </Box>
          )}

          {/* Documents Display */}
          {(filesLoading) ? (
            <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox disabled />
                    </TableCell>
                    <TableCell>Document</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>File Size</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <TableRowSkeleton key={index} columns={9} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : documents.length === 0 ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No documents found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeTab === 'project'
                  ? 'No project documents found. Upload documents to get started.'
                  : activeTab === 'deal'
                  ? 'No deal documents found. Upload documents to get started.'
                  : activeTab === 'ddq'
                  ? 'No DDQ documents found. Upload documents to get started.'
                  : 'Upload documents to get started'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedDocuments.size > 0 && selectedDocuments.size < documents.length}
                          checked={documents.length > 0 && selectedDocuments.size === documents.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDocuments(new Set(documents.map(d => d.id)));
                            } else {
                              setSelectedDocuments(new Set());
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={(activeTab === 'shared' ? sharedDocsSort : sortField).startsWith('name')}
                          direction={(activeTab === 'shared' ? sharedDocsSort : sortField).endsWith('asc') ? 'asc' : 'desc'}
                          onClick={() => {
                            const isAsc = (activeTab === 'shared' ? sharedDocsSort : sortField) === DocumentSort.NAME_ASC;
                            const newSortField = isAsc ? DocumentSort.NAME_DESC : DocumentSort.NAME_ASC;
                            if (activeTab === 'shared') {
                              setSharedDocsSort(newSortField);
                            } else {
                              setSortField(newSortField);
                            }
                          }}
                        >
                          Document
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>File Size</TableCell>
                      <TableCell>Tags</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={(activeTab === 'shared' ? sharedDocsSort : sortField).startsWith('uploaded')}
                        direction={(activeTab === 'shared' ? sharedDocsSort : sortField).endsWith('asc') ? 'asc' : 'desc'}
                        onClick={() => {
                          const isAsc = (activeTab === 'shared' ? sharedDocsSort : sortField) === DocumentSort.UPLOADED_ASC;
                          const newSortField = isAsc ? DocumentSort.UPLOADED_DESC : DocumentSort.UPLOADED_ASC;
                          if (activeTab === 'shared') {
                            setSharedDocsSort(newSortField);
                          } else {
                            setSortField(newSortField);
                          }
                        }}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc, index) => {
                      const isItemSelected = isSelected(doc.id);
                      return (
                        <Fade in timeout={100 + index * 50} key={doc.id}>
                          <TableRow
                            hover
                            onClick={(event) => handleClick(event, doc.id)}
                            onContextMenu={(event) => handleContextMenu(event, doc)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            selected={isItemSelected}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={isItemSelected} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getFileIcon(doc.type || '')}
                                <Box>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontWeight: 500 }}>
                                    {doc.filename || doc.name || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatBytes(doc.file_size || 0)}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.type ? doc.type.toUpperCase() : 'N/A'}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {formatBytes(doc.file_size || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 200 }}>
                                {(doc as any).tags && (doc as any).tags.length > 0 ? (
                                  (doc as any).tags.slice(0, 2).map((tag: string, index: number) => (
                                    <Chip
                                      key={index}
                                      label={tag}
                                      size="small"
                                      color={tag === 'deal' ? 'primary' : tag === 'project' ? 'secondary' : 'default'}
                                      variant="outlined"
                                    />
                                  ))
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    No tags
                                  </Typography>
                                )}
                                {(doc as any).tags && (doc as any).tags.length > 2 && (
                                  <Chip label={`+${(doc as any).tags.length - 2}`} size="small" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{getStatusChip(doc.embedding_status as any)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {(doc as any).source_type === 'project' && <FolderIcon fontSize="small" color="secondary" />}
                                {(doc as any).source_type === 'deal' && <BusinessIcon fontSize="small" color="primary" />}
                                {(doc as any).source_type === 'general' && <DocumentIcon fontSize="small" color="action" />}
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {doc.source || 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(doc.uploaded_at as any).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Tooltip title="View PDF">
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenPdfViewer(doc);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {(doc as any).project_id && (
                                  <Tooltip title="Chat with AI">
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        const projectId = (doc as any).project_id;
                                        
                                        // Set the project and document in localStorage for the Chat page
                                        localStorage.setItem('chat_selected_project', projectId.toString());
                                        localStorage.setItem('chat_selected_documents', JSON.stringify([doc.id]));
                                        
                                        // Navigate to chat page
                                        navigate('/chat');
                                      }}
                                    >
                                      <ChatIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDownload(doc);
                                    }}
                                    disabled={downloadingDocIds.has(doc.id)}
                                  >
                                    {downloadingDocIds.has(doc.id) ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="More Actions">
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      // For the three-dot icon, position the menu relative to the button
                                      const rect = event.currentTarget.getBoundingClientRect();
                                      // Pass position directly instead of creating a fake event
                                      handleContextMenu(
                                        null,
                                        doc,
                                        { x: rect.right, y: rect.bottom }
                                      );
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        </Fade>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {activeTab === 'shared' ? (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalSharedDocuments}
                  rowsPerPage={sharedDocsRowsPerPage}
                  page={sharedDocsPage}
                  onPageChange={(e, newPage) => setSharedDocsPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setSharedDocsRowsPerPage(parseInt(e.target.value, 10));
                    setSharedDocsPage(0);
                  }}
                  sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    '& .MuiTablePagination-toolbar': {
                      px: 2,
                    },
                    flexShrink: 0, // Ensure pagination doesn't shrink
                  }}
                />
              ) : (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalDocuments}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    '& .MuiTablePagination-toolbar': {
                      px: 2,
                    },
                    flexShrink: 0, // Ensure pagination doesn't shrink
                  }}
                />
              )}
            </>
          )}
        </Paper>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={() => {
            if (contextMenu?.document) {
              handleOpenPdfViewer(contextMenu.document);
            }
            handleCloseContextMenu();
          }}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View PDF</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (contextMenu?.document) {
                handleDownload(contextMenu.document);
              }
              handleCloseContextMenu();
            }}
            disabled={!!(contextMenu?.document && downloadingDocIds.has(contextMenu.document.id))}
          >
            <ListItemIcon>
              {contextMenu?.document && downloadingDocIds.has(contextMenu.document.id) ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {contextMenu?.document && downloadingDocIds.has(contextMenu.document.id) ?
                'Downloading...' : 'Download'}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (contextMenu?.document) {
              if (contextMenu.document.source === 'deal') {
                handleOpenDealShareCopyDialog(contextMenu.document);
              } else {
                handleOpenShareDialog(contextMenu.document);
              }
            }
            handleCloseContextMenu();
          }}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (contextMenu?.document) {
              if (contextMenu.document.source === 'deal') {
                handleOpenDealShareCopyDialog(contextMenu.document);
              } else {
                handleOpenCopyDialog(contextMenu.document);
              }
            }
            handleCloseContextMenu();
          }}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy to Project</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              if (contextMenu?.document) {
                handleDeleteDocument(contextMenu.document.id);
              }
              handleCloseContextMenu();
            }}
            sx={{ color: 'error.main' }}
            disabled={isDeleting}
          >
            <ListItemIcon>
              {isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" color="error" />}
            </ListItemIcon>
            <ListItemText>{isDeleting ? 'Deleting...' : 'Delete'}</ListItemText>
          </MenuItem>
        </Menu>

        {/* Document Details Modal */}
        <DocumentDetailsModal
          document={selectedDocument}
          open={isModalOpen}
          onClose={handleCloseModal}
        />

        {/* PDF Viewer Modal */}
        <Dialog
          open={pdfViewerOpen}
          onClose={handleClosePdfViewer}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                {pdfDocument?.filename || pdfDocument?.name || 'Document'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={zoomOut} size="small">
                    <Typography variant="body2">-</Typography>
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
                  {Math.round(pdfScale * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={zoomIn} size="small">
                    <Typography variant="body2">+</Typography>
                  </IconButton>
                </Tooltip>
                <IconButton onClick={handleClosePdfViewer} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, overflow: 'auto' }}>
            {pdfDocument && pdfUrl ? (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <PdfDocument
                  file={pdfUrl}
                  onLoadSuccess={onPdfLoadSuccess}
                  onLoadError={onPdfLoadError}
                  loading={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                      <CircularProgress />
                    </Box>
                  }
                  error={
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="error">Failed to load PDF</Typography>
                      <Button onClick={() => handleOpenModal(pdfDocument)} sx={{ mt: 2 }}>
                        View Details Instead
                      </Button>
                    </Box>
                  }
                >
                  <Page
                    pageNumber={pdfPageNumber}
                    scale={pdfScale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </PdfDocument>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                onClick={() => changePdfPage(-1)}
                disabled={pdfPageNumber <= 1}
                startIcon={<ChevronLeftIcon />}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {pdfPageNumber} of {pdfNumPages || '?'}
              </Typography>
              <Button
                onClick={() => changePdfPage(1)}
                disabled={!pdfNumPages || pdfPageNumber >= pdfNumPages}
                endIcon={<ChevronRightIcon />}
              >
                Next
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => pdfDocument && handleDownload(pdfDocument)}
              >
                Download
              </Button>
              <Button onClick={handleClosePdfViewer}>Close</Button>
            </Box>
          </DialogActions>
        </Dialog>

        <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="lg" fullWidth>
          <DialogTitle>Upload Document(s)</DialogTitle>
          <DialogContent>
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={uploadTabValue} onChange={handleUploadTabChange}>
            <Tab label="Upload New" icon={<UploadFileIcon />} iconPosition="start" />
            <Tab label="Integrations" icon={<CloudUploadIcon />} iconPosition="start" />
        </Tabs>
    </Box>
    {uploadTabValue === 0 && (
        <Box sx={{ pt: 2 }}>
            {selectedFiles.length === 0 ? (
                <Box
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragging ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        mt: 2,
                        bgcolor: isDragging ? 'action.hover' : 'transparent',
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragEvents}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleSelectFileClick}
                >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                        Drag & drop files here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        or click to select files
                    </Typography>
                </Box>
            ) : (
                <Box>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Document Type</InputLabel>
                        <Select
                            value={documentType}
                            label="Document Type"
                            onChange={(e) => setDocumentType(e.target.value as string)}
                        >
                            <MenuItem value="lease">Lease</MenuItem>
                            <MenuItem value="jv_agreement">JV Agreement</MenuItem>
                            <MenuItem value="psa">PSA</MenuItem>
                            <MenuItem value="loan_doc">Loan Doc</MenuItem>
                            <MenuItem value="offering_memorandum">Offering Memorandum</MenuItem>
                            <MenuItem value="sponsor_model">Sponsor Model</MenuItem>
                            <MenuItem value="ddq">DDQ</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mt: 2 }} required={documentType === 'ddq'}>
                        <InputLabel>Project {documentType === 'ddq' ? '(Required)' : '(Optional)'}</InputLabel>
                        <Select
                            value={uploadSelectedProject}
                            label={`Project ${documentType === 'ddq' ? '(Required)' : '(Optional)'}`}
                            onChange={(e) => setUploadSelectedProject(e.target.value as string)}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>
                                    {project.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {`${selectedFiles.length} file(s) selected`}
                        </Typography>
                        {selectedFiles.length > 1 && (
                            <Button size="small" onClick={handleClearAllFiles}>Clear all</Button>
                        )}
                    </Box>
                    <Stack spacing={1.5}>
                        {selectedFiles.map((file, index) => {
                            const fileStatus = uploadStatus.get(file.name) || 'pending';
                            const fileProgress = uploadProgress.get(file.name) || 0;
                            
                            return (
                                <Paper
                                    key={index}
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor: 'background.default',
                                        borderColor: fileStatus === 'success' ? 'success.main' : 
                                                    fileStatus === 'error' ? 'error.main' :
                                                    fileStatus === 'uploading' ? 'primary.main' : 'divider'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: fileStatus === 'uploading' ? 1 : 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: 1 }}>
                                            <Avatar sx={{ 
                                                bgcolor: fileStatus === 'success' ? 'success.light' :
                                                        fileStatus === 'error' ? 'error.light' :
                                                        fileStatus === 'uploading' ? 'primary.light' : 'primary.light',
                                                mr: 1.5 
                                            }}>
                                                {fileStatus === 'uploading' ? (
                                                    <CircularProgress size={20} sx={{ color: 'white' }} />
                                                ) : (
                                                    <FilePresentIcon sx={{ color: 'white' }} />
                                                )}
                                            </Avatar>
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatBytes(file.size)}
                                                    {fileStatus === 'uploading' && ` • Uploading ${fileProgress}%`}
                                                    {fileStatus === 'success' && ' • ✓ Uploaded'}
                                                    {fileStatus === 'error' && ' • ✗ Failed'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {!isUploading && (
                                            <IconButton size="small" onClick={() => handleRemoveFile(file)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {fileStatus === 'uploading' && (
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={fileProgress} 
                                            sx={{ 
                                                height: 6, 
                                                borderRadius: 1,
                                                bgcolor: 'action.hover'
                                            }}
                                        />
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    )}
    {uploadTabValue === 1 && (
        <Box sx={{ pt: 2 }}>
            {selectedIntegration ? (
                <>
                    {integrationFilesLoading ? (
                        <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {folderPath.map((folder, index) => (
                                        <React.Fragment key={folder.id}>
                                            {index < folderPath.length - 1 ? (
                                                <Button
                                                    onClick={() => {
                                                        const newPath = folderPath.slice(0, index + 1);
                                                        setFolderPath(newPath);
                                                        setCurrentFolderId(folder.id);
                                                        if (selectedIntegration) {
                                                            fetchIntegrationFiles(selectedIntegration, folder.id);
                                                        }
                                                    }}
                                                >
                                                    {folder.name}
                                                </Button>
                                            ) : (
                                                <Typography variant="h6" sx={{ color: 'text.primary' }}>{folder.name}</Typography>
                                            )}
                                            {index < folderPath.length - 1 && <ChevronRightIcon />}
                                        </React.Fragment>
                                    ))}
                                </Box>
                                <Box>
                                    <Tooltip title="Refresh files">
                                        <IconButton onClick={() => fetchIntegrationFiles(selectedIntegration, currentFolderId)}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Button variant="outlined" startIcon={<ChevronLeftIcon />} onClick={() => {
                                      setSelectedIntegration(null);
                                      setSelectedIntegrationFilesForUpload([]);
                                      setCurrentFolderId('root');
                                      setFolderPath([{ id: 'root', name: 'My Drive' }]);
                                    }} sx={{ ml: 1 }}>Back to Integrations</Button>
                                </Box>
                            </Box>
                            <Paper sx={{ maxHeight: 250, overflow: 'auto' }}>
                                <TableContainer>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox"></TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Modified</TableCell>
                                                <TableCell>Size</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {isSubfolderLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        <CircularProgress />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                integrationFiles.map((file) => (
                                            <TableRow
                                                key={file.id}
                                                hover
                                                sx={{
                                                    cursor: file.isFolder ? 'pointer' : 'default',
                                                    '&:hover': file.isFolder ? {
                                                        bgcolor: 'action.hover',
                                                    } : {}
                                                }}
                                                onClick={() => {
                                                    if (file.isFolder) {
                                                        setCurrentFolderId(file.id);
                                                        setFolderPath([...folderPath, { id: file.id, name: file.name }]);
                                                        if (selectedIntegration) {
                                                            fetchIntegrationFiles(selectedIntegration, file.id);
                                                        }
                                                    }
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    {!file.isFolder && (
                                                        <Checkbox
                                                            checked={selectedIntegrationFilesForUpload.some(f => f.id === file.id)}
                                                            onChange={(e) => {
                                                                handleIntegrationFileSelect(file, e.target.checked);
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {file.isFolder ? (
                                                            <>
                                                                <FolderIcon fontSize="small" color="primary" />
                                                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                                    {file.name}
                                                                </Typography>
                                                                <ChevronRightIcon fontSize="small" color="action" />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <DocumentIcon fontSize="small" color="action" />
                                                                <Typography variant="body2" noWrap>
                                                                    {file.name}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption">
                                                                {file.modified_time ? new Date(file.modified_time).toLocaleDateString() : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption">
                                                                {file.size ? formatBytes(file.size) : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </>
                    )}
                </>
            ) : (
                <>
                    {integrationsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : noIntegrationsConnected ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No Integrations Connected
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                It looks like you haven't connected any cloud storage integrations yet.
                                Connect your accounts to seamlessly upload documents.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    handleCloseUploadDialog();
                                    navigate('/integrations'); // Navigate to the integrations page
                                }}
                            >
                                Go to Integrations
                            </Button>
                        </Box>
                    ) : (
                        <List>
                            {integrations.map((integration) => (
                                <ListItem
                                    key={integration.id}
                                    secondaryAction={
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={!integration.isConnected}
                                            onClick={() => {
                                                setSelectedIntegration(integration);
                                                const integrationName = integration.name.toLowerCase();
                                                let initialFolderId = 'root';
                                                if (integrationName === 'dropbox') {
                                                    initialFolderId = '';
                                                } else if (integrationName === 'box') {
                                                    initialFolderId = '0';
                                                }
                                                setCurrentFolderId(initialFolderId);
                                                setFolderPath([{ id: initialFolderId, name: integration.name }]);
                                            }}
                                        >
                                            {integration.isConnected ? 'Open' : 'Connect'}
                                        </Button>
                                    }
                                >
                                    <ListItemIcon>
                                        <img
                                            src={`/logos/${integration.name.toLowerCase().replace(/\s/g, '')}.png`}
                                            alt={`${integration.name} logo`}
                                            width="24"
                                            height="24"
                                            style={{ objectFit: 'contain' }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={integration.name}
                                        secondary={
                                            integration.isConnected ? (
                                                <Typography variant="caption" color="green">
                                                    Connected
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Disconnected
                                                </Typography>
                                            )
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </>
            )}
        </Box>
    )}
    {selectedIntegrationFilesForUpload.length > 0 && (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {`${selectedIntegrationFilesForUpload.length} file(s) selected from integrations`}
          </Typography>
          <Button size="small" onClick={() => setSelectedIntegrationFilesForUpload([])}>Clear all</Button>
        </Box>
        <Stack spacing={1.5}>
          {selectedIntegrationFilesForUpload.map((file, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'background.default'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <Avatar sx={{ bgcolor: 'secondary.light', mr: 1.5 }}>
                  <FilePresentIcon sx={{ color: 'white' }} />
                </Avatar>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(file.size)} ({file.source})
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setSelectedIntegrationFilesForUpload(p => p.filter(f => f.id !== file.id))}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Stack>
      </Box>
    )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseUploadDialog}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={uploadTabValue === 0 ? handleUpload : handleIntegrationUpload}
              disabled={
                (uploadTabValue === 0 && selectedFiles.length === 0) ||
                (uploadTabValue === 1 && selectedIntegrationFilesForUpload.length === 0) ||
                isUploading ||
                (documentType === 'ddq' && !uploadSelectedProject)
              }
              startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
            >
              {isUploading ? 'Uploading...' : `Upload ${uploadTabValue === 0 ? selectedFiles.length : selectedIntegrationFilesForUpload.length} File(s)`}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      {/* Password Modal */}
      <Dialog open={passwordPrompt !== null} onClose={handlePasswordCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Password Protected PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This PDF requires a password to access. Please enter the password to continue.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
            File: {passwordPrompt?.fileName}
          </Typography>
          <TextField
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            placeholder="Enter PDF password"
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordCancel} disabled={isCheckingPassword}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!password.trim() || isCheckingPassword}
            startIcon={isCheckingPassword ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isCheckingPassword ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Share Document</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Share With</InputLabel>
            <Select
              value={selectedMember}
              label="Share With"
              onChange={(e) => setSelectedMember(e.target.value as string)}
              IconComponent={isFetchingMembers ? () => null : undefined}
              endAdornment={
                isFetchingMembers ? (
                  <InputAdornment position="end" sx={{ marginRight: '5px' }}>
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }
            >
              {members.map((member: any) => (
                <MenuItem key={member.id} value={member.user_id}>
                  {member.user_email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog} disabled={isSharing}>Cancel</Button>
          <Button
            onClick={handleShareDocument}
            variant="contained"
            disabled={isSharing || !selectedMember}
            startIcon={isSharing ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
          >
            {isSharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Share Dialog */}
      <Dialog open={bulkShareDialogOpen} onClose={handleCloseBulkShareDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Share Selected Documents</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Share With</InputLabel>
            <Select
              value={selectedMember}
              label="Share With"
              onChange={(e) => setSelectedMember(e.target.value as string)}
              IconComponent={isFetchingMembers ? () => null : undefined}
              endAdornment={
                isFetchingMembers ? (
                  <InputAdornment position="end" sx={{ marginRight: '5px' }}>
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }
            >
              {members.map((member: any) => (
                <MenuItem key={member.id} value={member.user_id}>
                  {member.user_email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkShareDialog} disabled={isSharing}>Cancel</Button>
          <Button
            onClick={handleBulkShareDocuments}
            variant="contained"
            disabled={isSharing || !selectedMember || selectedDocuments.size === 0}
            startIcon={isSharing ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
          >
            {isSharing ? 'Sharing...' : `Share ${selectedDocuments.size} Documents`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Document Dialog */}
      {/* Single Copy Document Dialog */}
      <Dialog open={copyDialogOpen} onClose={handleCloseCopyDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Copy Document to Project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the project where you want to copy the document "{documentToCopy?.filename || documentToCopy?.name}".
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Target Project</InputLabel>
            <Select
              value={targetProject}
              label="Target Project"
              onChange={(e) => setTargetProject(e.target.value as string)}
              IconComponent={projectsLoading ? () => null : undefined}
              endAdornment={
                projectsLoading ? (
                  <InputAdornment position="end" sx={{ marginRight: '5px' }}>
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projectsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} /> Loading Projects...
                </MenuItem>
              ) : (
                projects
                  .filter(project => project.id !== documentToCopy?.project_id)
                  .map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCopyDialog} disabled={isCopying}>Cancel</Button>
          <Button
            onClick={handleCopyDocument}
            variant="contained"
            disabled={!targetProject || !documentToCopy || isCopying}
            startIcon={isCopying ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isCopying ? 'Copying...' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Copy Document Dialog */}
      <Dialog open={bulkCopyDialogOpen} onClose={handleCloseBulkCopyDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Copy Document(s) to Project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the project where you want to copy the selected document(s).
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Target Project</InputLabel>
            <Select
              value={targetProject}
              label="Target Project"
              onChange={(e) => setTargetProject(e.target.value as string)}
              IconComponent={projectsLoading ? () => null : undefined}
              endAdornment={
                projectsLoading ? (
                  <InputAdornment position="end" sx={{ marginRight: '5px' }}>
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projectsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} /> Loading Projects...
                </MenuItem>
              ) : (
                projects
                  .filter(project => project.id !== documentToCopy?.project_id)
                  .map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkCopyDialog} disabled={isCopying}>Cancel</Button>
          <Button
            onClick={handleBulkCopyToProject}
            variant="contained"
            disabled={!targetProject || selectedDocuments.size === 0 || isCopying}
            startIcon={isCopying ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isCopying ? 'Copying...' : `Copy ${selectedDocuments.size} Document(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deal Share/Copy Dialog */}
      <Dialog open={dealShareCopyDialogOpen} onClose={handleCloseDealShareCopyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Share/Copy Deal Document</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This document is associated with a deal. You can share deal or convert deal to project from the deal details page.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Go to the deal details page to manage sharing and copying options for this deal document.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDealShareCopyDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (dealShareCopyDocument?.deal_id) {
                navigate(`/deals/${dealShareCopyDocument.deal_id}`);
                handleCloseDealShareCopyDialog();
              }
            }}
            disabled={!dealShareCopyDocument?.deal_id}
          >
            Go to Deal Page
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentWorkspace;
